/**
 * The hub's kitchen display board: the KOT tickets terminals relay, per station, with the
 * KDS actions (start, bump, recall). It answers in the same shape as the cloud's
 * `GET {o}/kds/tickets` (`kdsTicketsSchema`) so a KDS screen shows the same tickets
 * whichever source it reads. Adding a ticket id twice changes nothing.
 */
import type { KdsTicketView } from '@qrkey/contracts';

export interface KdsRecord {
  ticket: KdsTicketView;
  /** Last change, for merging replicas (the later one wins). */
  updatedAt: number;
}

export type KdsAction = 'start' | 'bump' | 'recall';

export const RECENT_BUMP_MS = 3 * 60 * 60 * 1000;
const DONE = new Set(['ready', 'served', 'void']);

export class KdsBoard {
  readonly #tickets = new Map<string, KdsRecord>();

  constructor(records: readonly KdsRecord[] = []) {
    for (const r of records) this.#tickets.set(r.ticket.id, { ...r });
  }

  add(ticket: KdsTicketView, now: number): boolean {
    if (this.#tickets.has(ticket.id)) return false;
    this.#tickets.set(ticket.id, { ticket, updatedAt: now });
    return true;
  }

  get(id: string): KdsTicketView | undefined {
    return this.#tickets.get(id)?.ticket;
  }

  act(
    id: string,
    action: KdsAction,
    nowMs: number,
    lineIds?: readonly string[],
  ): KdsTicketView | null {
    const rec = this.#tickets.get(id);
    if (!rec) return null;
    const t = rec.ticket;
    const stamp = new Date(nowMs).toISOString();
    if (action === 'start') {
      rec.ticket = {
        ...t,
        lines: t.lines.map((l) => (l.status === 'sent' ? { ...l, status: 'preparing' } : l)),
      };
    } else if (action === 'bump') {
      if (t.bumpedAt !== null) return t;
      const only = lineIds ? new Set(lineIds) : null;
      const lines = t.lines.map((l) =>
        l.status === 'void' || (only && !only.has(l.orderLineId)) ? l : { ...l, status: 'ready' },
      );
      // Bumping every item bumps the ticket; bumping some leaves it on the screen.
      const all = lines.every((l) => DONE.has(l.status));
      rec.ticket = { ...t, lines, bumpedAt: all ? stamp : null };
    } else {
      rec.ticket = {
        ...t,
        bumpedAt: null,
        lines: t.lines.map((l) =>
          DONE.has(l.status) && l.status !== 'void' ? { ...l, status: 'preparing' } : l,
        ),
      };
    }
    rec.updatedAt = nowMs;
    return rec.ticket;
  }

  list(stationId: string | null, nowMs: number) {
    const mine = [...this.#tickets.values()]
      .map((r) => r.ticket)
      .filter((t) => stationId === null || t.stationId === stationId);
    const byAge = (a: KdsTicketView, b: KdsTicketView) => a.createdAt.localeCompare(b.createdAt);
    return {
      now: new Date(nowMs).toISOString(),
      open: mine.filter((t) => t.bumpedAt === null).sort(byAge),
      recentlyBumped: mine
        .filter((t) => t.bumpedAt !== null && nowMs - Date.parse(t.bumpedAt) < RECENT_BUMP_MS)
        .sort((a, b) => (b.bumpedAt ?? '').localeCompare(a.bumpedAt ?? '')),
    };
  }

  merge(records: readonly KdsRecord[]): KdsTicketView[] {
    const changed: KdsTicketView[] = [];
    for (const theirs of records) {
      const mine = this.#tickets.get(theirs.ticket.id);
      if (!mine || theirs.updatedAt > mine.updatedAt) {
        this.#tickets.set(theirs.ticket.id, { ...theirs });
        changed.push(theirs.ticket);
      }
    }
    return changed;
  }

  /** Drop bumped tickets older than the recall window (keeps the file small). */
  prune(nowMs: number): void {
    for (const [id, r] of this.#tickets) {
      if (r.ticket.bumpedAt !== null && nowMs - Date.parse(r.ticket.bumpedAt) > RECENT_BUMP_MS) {
        this.#tickets.delete(id);
      }
    }
  }

  snapshot(): KdsRecord[] {
    return [...this.#tickets.values()].map((r) => ({ ...r }));
  }
}
